import * as React from 'react'

import { aggregateSkillStatusPerStudentData, skillStatusStudentNameTooltipText, skillStatusPreQuestionsCorrectTooltipText, skillStatusPreSkillStatusTooltipText, activitiesAndTimespentTooltipText, skillStatusPostQuestionsCorrectTooltipText, skillStatusPostSkillStatusTooltipText } from './helpers'

import { Spinner, DataTable, noResultsMessage, DropdownInput } from '../../../Shared/index'
import useScaledTableColumnWidths from '../../../Shared/hooks/useScaledTableColumnWidths'
import { DropdownObjectInterface } from '../../../Staff/interfaces/evidenceInterfaces'
import { getDiagnosticTypeDropdownOptions, hashPayload } from '../../shared'
import { requestPost } from '../../../../modules/request'

const SKILLS_QUERY_KEY = 'diagnostic-skills'
const SKILLS_PUSHER_EVENT_KEY = 'admin-diagnostic-skills-cached'
const STUDENT_QUERY_KEY = 'diagnostic-skills-per-student'
const RECOMMENDATIONS_QUERY_KEY = 'student-recommendation'
const STUDENT_PUSHER_EVENT_KEY = 'admin-diagnostic-skills-per-student-cached'
const RECOMMENDATIONS_PUSHER_EVENT_KEY = 'admin-diagnostic-students-cached'
const BATCH_SIZE = 50
const MAX_ROW_COUNT = 500

export const POST_SKILL_STATUS_OPTIONS: DropdownObjectInterface[] = [
  { label: 'Improved Skill', value: 'improved' },
  { label: 'Maintained Skill', value: 'maintained' },
  { label: 'Skill to Practice', value: 'to_practice' },
]

// Column widths for scaling
const COLUMN_WIDTHS = [
  { width: 190 },  // Student Name
  { width: 190 },  // Pre: Questions Correct
  { width: 190 },  // Pre: Skill Status
  { width: 190 },  // Activities & Time Spent on Skill
  { width: 190 },  // Post: Questions Correct
  { width: 190 },  // Post: Skill Status
]

const MIN_TABLE_WIDTH = COLUMN_WIDTHS.reduce((sum, col) => sum + col.width, 0)
const MAX_TABLE_WIDTH = 1834

const getHeaders = (scaledWidths) => [
  {
    name: 'Student Name',
    attribute: 'name',
    sortAttribute: 'studentName',
    tooltipName: 'Student Name',
    tooltipDescription: skillStatusStudentNameTooltipText,
    width: scaledWidths['190w'],
    noTooltip: true,
    isSortable: true,
    renderCellsAsHeaders: true
  },
  {
    name: '',
    ariaLabel: 'Pre: Questions Correct',
    attribute: 'preQuestionsCorrect',
    sortAttribute: 'preQuestionsCorrectSortValue',
    width: scaledWidths['190w'],
    primaryTitle: 'Pre: Questions',
    secondaryTitle: 'Correct',
    tooltipName: 'Pre: Questions Correct',
    tooltipDescription: skillStatusPreQuestionsCorrectTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Pre: Skill Status',
    attribute: 'preSkillStatus',
    sortAttribute: 'preSkillsProficientSortValue',
    width: scaledWidths['190w'],
    primaryTitle: 'Pre: Skill',
    secondaryTitle: 'Status',
    tooltipName: 'Pre: Skill Status',
    tooltipDescription: skillStatusPreSkillStatusTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Activities & Time Spent on Skill',
    attribute: 'activitiesAndTimespent',
    sortAttribute: 'totalActivities',
    width: scaledWidths['190w'],
    primaryTitle: 'Activities &',
    secondaryTitle: 'Time Spent on Skill',
    tooltipName: 'Activities & Time Spent on Skill',
    tooltipDescription: activitiesAndTimespentTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Post: Questions Correct',
    attribute: 'postQuestionsCorrect',
    sortAttribute: 'postQuestionsCorrectSortValue',
    width: scaledWidths['190w'],
    primaryTitle: 'Post: Questions',
    secondaryTitle: 'Correct',
    tooltipName: 'Post: Questions Correct',
    tooltipDescription: skillStatusPostQuestionsCorrectTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Post: Skill Status',
    attribute: 'postSkillStatus',
    width: scaledWidths['190w'],
    primaryTitle: 'Post: Skill',
    secondaryTitle: 'Status',
    tooltipName: 'Post: Skill Status',
    tooltipDescription: skillStatusPostSkillStatusTooltipText,
    noTooltip: true,
    isSortable: false
  },
]

export const SkillStatusPerStudentSection = ({
  searchCount,
  selectedGrades,
  selectedSchoolIds,
  selectedTeacherIds,
  selectedClassroomIds,
  selectedTimeframe,
  diagnosticTypeValue,
  pusherChannel,
  createCsvReportDownload,
  handleSetDiagnosticIdForStudentCount,
  handleSetSelectedDiagnosticType,
  selectedSkillValue,
  selectedPostSkillStatuses,
  handleSetSelectedSkillValue,
  handleSetSelectedPostSkillStatuses,
  passedData,
  horizontalFilters,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [pusherMessage, setPusherMessage] = React.useState<string>(null)

  const scaledWidths = useScaledTableColumnWidths({
    columnWidths: COLUMN_WIDTHS,
    containerRef,
    maxTableWidth: MAX_TABLE_WIDTH,
    minTableWidth: MIN_TABLE_WIDTH,
  })

  const headers = getHeaders(scaledWidths)
  const [availableSkills, setAvailableSkills] = React.useState<DropdownObjectInterface[]>([])
  const [recommendationsData, setRecommendationsData] = React.useState<any>(null)
  const [studentData, setStudentData] = React.useState<any>(null)
  const [totalStudents, setTotalStudents] = React.useState<number>(null)
  const [visibleData, setVisibleData] = React.useState<any>(passedData || [])
  const [rowsToShow, setRowsToShow] = React.useState<number>(BATCH_SIZE)
  const [loading, setLoading] = React.useState<boolean>(!passedData)

  React.useEffect(() => {
    handleSetDiagnosticIdForStudentCount(null)
  }, [])

  React.useEffect(() => {
    initializePusher()
  }, [pusherChannel])

  React.useEffect(() => {
    if (passedData || !diagnosticTypeValue) return

    const diagnosticTypeDropdownOptions = getDiagnosticTypeDropdownOptions(selectedTimeframe)

    if (!diagnosticTypeDropdownOptions.includes(diagnosticTypeValue)) {
      const selectedDiagnosticType = diagnosticTypeDropdownOptions.find(
        (option) => option.label === diagnosticTypeValue.label
      )
      handleSetSelectedDiagnosticType(selectedDiagnosticType)
      return
    }
    getSkillsList()
  }, [searchCount, diagnosticTypeValue])

  React.useEffect(() => {
    if (availableSkills.length === 0 || selectedSkillValue) return

    handleSetSelectedSkillValue(availableSkills[0])
  }, [availableSkills, selectedSkillValue])

  const resolvedSkillValue = React.useMemo(() => {
    if (!selectedSkillValue) { return null }
    if (selectedSkillValue.label) { return selectedSkillValue }
    return availableSkills.find(s => s.value === selectedSkillValue.value) || selectedSkillValue
  }, [selectedSkillValue, availableSkills])

  React.useEffect(() => {
    if (!passedData && diagnosticTypeValue && selectedSkillValue?.value) {
      resetStudentData()
      getStudentData()
      getRecommendationsData()
    }
  }, [searchCount, diagnosticTypeValue, selectedSkillValue])

  React.useEffect(() => {
    if (!studentData) { return }

    const allFormatted = aggregateSkillStatusPerStudentData(studentData, recommendationsData)
    if (!allFormatted) { return }

    const filtered = filterByPostSkillStatus(allFormatted)
    updateVisibleData(filtered)
    setLoading(false)
  }, [studentData, recommendationsData])

  React.useEffect(() => {
    if (!studentData) { return }
    const allFormatted = aggregateSkillStatusPerStudentData(studentData, recommendationsData)
    if (allFormatted) {
      const filtered = filterByPostSkillStatus(allFormatted)
      updateVisibleData(filtered)
    }
  }, [selectedPostSkillStatuses, rowsToShow])

  React.useEffect(() => {
    if (!pusherMessage || !diagnosticTypeValue) return
    if (pusherMessage === getSkillsFilterHash()) { getSkillsList() }
    if (selectedSkillValue && pusherMessage === getStudentFilterHash()) { getStudentData() }
    if (pusherMessage === getRecommendationsFilterHash()) { getRecommendationsData() }
    setPusherMessage(null)
  }, [pusherMessage])

  function initializePusher() {
    pusherChannel?.bind(SKILLS_PUSHER_EVENT_KEY, (body) => { setPusherMessage(body.message) })
    pusherChannel?.bind(STUDENT_PUSHER_EVENT_KEY, (body) => { setPusherMessage(body.message) })
    pusherChannel?.bind(RECOMMENDATIONS_PUSHER_EVENT_KEY, (body) => { setPusherMessage(body.message) })
  }

  function getSharedFilterParams() {
    return {
      timeframe: selectedTimeframe,
      school_ids: selectedSchoolIds,
      teacher_ids: selectedTeacherIds,
      classroom_ids: selectedClassroomIds,
      grades: selectedGrades,
      diagnostic_id: diagnosticTypeValue.value
    }
  }

  function getSkillsFilterHash() {
    const filterTarget = [].concat(
      SKILLS_QUERY_KEY,
      'grade',
      parseInt(diagnosticTypeValue.value),
      selectedTimeframe,
      selectedSchoolIds,
      selectedGrades,
      selectedTeacherIds,
      selectedClassroomIds,
    )
    return hashPayload(filterTarget)
  }

  function getStudentFilterHash() {
    const filterTarget = [].concat(
      STUDENT_QUERY_KEY,
      parseInt(diagnosticTypeValue.value),
      selectedSkillValue.value,
      selectedTimeframe,
      selectedSchoolIds,
      selectedGrades,
      selectedTeacherIds,
      selectedClassroomIds,
    )
    return hashPayload(filterTarget)
  }

  function getRecommendationsFilterHash() {
    const filterTarget = [].concat(
      RECOMMENDATIONS_QUERY_KEY,
      parseInt(diagnosticTypeValue.value),
      selectedTimeframe,
      selectedSchoolIds,
      selectedGrades,
      selectedTeacherIds,
      selectedClassroomIds,
    )
    return hashPayload(filterTarget)
  }

  function getSkillsList() {
    const searchParams = {
      query: SKILLS_QUERY_KEY,
      group_by: 'grade',
      ...getSharedFilterParams()
    }

    requestPost('/admin_diagnostic_skills/report', searchParams, (body) => {
      if (!body.hasOwnProperty('results')) { return }
      const { results } = body
      const skillOptions = results
        .filter(entry => entry.aggregate_id === null && entry.skill_group_id != null)
        .map(entry => ({ label: entry.skill_group_name, value: entry.skill_group_id }))
        .filter((opt, idx, arr) => arr.findIndex(o => o.value === opt.value) === idx)
      setAvailableSkills(skillOptions)
      if (skillOptions.length === 0) { setLoading(false) }
    })
  }

  function getStudentData() {
    setLoading(true)
    const searchParams = {
      query: STUDENT_QUERY_KEY,
      skill_group_id: selectedSkillValue.value,
      ...getSharedFilterParams()
    }

    requestPost('/admin_diagnostic_skills_per_student/report', searchParams, (body) => {
      if (!body.hasOwnProperty('results')) { return }
      const { results } = body
      setStudentData(results)
      setTotalStudents(results.length)
    })
  }

  function getRecommendationsData() {
    const searchParams = {
      query: RECOMMENDATIONS_QUERY_KEY,
      ...getSharedFilterParams()
    }

    requestPost('/admin_diagnostic_students/report', searchParams, (body) => {
      if (!body.hasOwnProperty('results')) { return }
      const { results } = body
      setRecommendationsData(results)
    })
  }

  function resetStudentData() {
    setStudentData(null)
    setRecommendationsData(null)
    setVisibleData(passedData || [])
    setRowsToShow(BATCH_SIZE)
    setLoading(true)
    setPusherMessage(null)
  }

  function filterByPostSkillStatus(data) {
    if (!selectedPostSkillStatuses || selectedPostSkillStatuses.length === 0) { return data }
    const selectedValues = selectedPostSkillStatuses.map(s => s.value)
    return data.filter(row => {
      if (selectedValues.includes('improved') && row.post_skills_improved) { return true }
      if (selectedValues.includes('maintained') && row.post_skills_maintained) { return true }
      if (selectedValues.includes('to_practice') && row.post_skills_to_practice) { return true }
      return false
    })
  }

  function updateVisibleData(data) {
    setVisibleData(data.slice(0, rowsToShow))
  }

  function loadMoreRows() {
    const count = rowsToShow + BATCH_SIZE
    if (studentData && count > studentData.length) {
      setRowsToShow(studentData.length)
    } else {
      setRowsToShow((prevRows) => prevRows + BATCH_SIZE)
    }
  }

  function handleDiagnosticTypeOptionChange(option: DropdownObjectInterface) {
    handleSetSelectedDiagnosticType(option)
    handleSetSelectedSkillValue(null)
    setAvailableSkills([])
    setVisibleData([])
    setStudentData(null)
    setRecommendationsData(null)
    setPusherMessage(null)
    setLoading(true)
  }

  function handleSkillOptionChange(option: DropdownObjectInterface) {
    handleSetSelectedSkillValue(option)
  }

  function handlePostSkillStatusChange(options: DropdownObjectInterface[]) {
    handleSetSelectedPostSkillStatuses(options)
  }

  function renderButtonContent() {
    const allFormatted = aggregateSkillStatusPerStudentData(studentData, recommendationsData)
    const filtered = allFormatted ? filterByPostSkillStatus(allFormatted) : []
    const totalToShow = filtered.length
    const disabled = rowsToShow >= totalToShow
    const disabledClass = disabled ? 'disabled contained' : 'outlined'
    const showDownloadLink = rowsToShow >= MAX_ROW_COUNT
    return (
      <div className="load-more-button-container">
        <div className="text-container">
          <p>Displaying <strong>{`${Math.min(rowsToShow, totalToShow)} of ${totalStudents || totalToShow}`}</strong> students</p>
          {showDownloadLink && <p> • Please <button className="interactive-wrapper" onClick={createCsvReportDownload} type="button">download this report</button> to view more than 500 results.</p>}
        </div>
        <button className={`quill-button-archived small secondary focus-on-light ${disabledClass}`} disabled={disabled} onClick={loadMoreRows} type="button">Load more</button>
      </div>
    )
  }

  function renderContent() {
    if (loading) {
      return <Spinner />
    }
    return (
      <div ref={containerRef}>
        <DataTable
          className="growth-diagnostic-reports-by-skill-status-per-student-table"
          emptyStateMessage={noResultsMessage('diagnostic')}
          headers={headers}
          reportingFormat={true}
          rows={visibleData}
        />
        {studentData && renderButtonContent()}
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="dropdowns-container">
        <DropdownInput
          className="diagnostic-type-dropdown bordered small focus-on-light"
          handleChange={handleDiagnosticTypeOptionChange}
          isSearchable={true}
          label="Diagnostic:"
          options={getDiagnosticTypeDropdownOptions(selectedTimeframe)}
          value={diagnosticTypeValue}
        />
        <DropdownInput
          className="skill-dropdown bordered small focus-on-light"
          handleChange={handleSkillOptionChange}
          isSearchable={true}
          label="Skill:"
          options={availableSkills}
          value={resolvedSkillValue}
        />
        <DropdownInput
          className="post-skill-status-dropdown bordered small focus-on-light"
          handleChange={handlePostSkillStatusChange}
          isMulti={true}
          isSearchable={true}
          label="Post Skill Status:"
          options={POST_SKILL_STATUS_OPTIONS}
          optionType="status"
          optionTypePlural="statuses"
          value={selectedPostSkillStatuses}
        />
      </div>
      {horizontalFilters}
      {renderContent()}
    </React.Fragment>
  )
}

export default SkillStatusPerStudentSection
